/*!
 * Minimal, conservative Markdown -> UA/JSON renderer (our own code; no deps).
 * Goal: render the EGT Codex VERBATIM (text never dropped or altered) with a
 * skin + section anchors/TOC. Strings become text nodes (auto-escaped by the
 * renderer), so legal text is preserved exactly. Handles headings, paragraphs,
 * lists, blockquotes, hr, inline code/links/bold/italic, fenced code.
 * NOTE: proof the rendered output against the source before go-live.
 */

function slug(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);
}

// Heading ids (founder ruling 2026-07-22 — "regenerate the ids ... I want real
// id numbers not fake ones"). A CSS identifier may not begin with a digit, so
// `1.1 Definitions` -> `1-1-definitions` made `querySelector('#1-1-definitions')`
// throw. A fixed `sec-` prefix makes every id valid while the REAL section
// number is preserved verbatim and the descriptive tail is kept for readability:
//   "1.1 Definitions"        -> "sec-1-1-definitions"
//   "Part 1 - Foundations"   -> "sec-part-1-foundations"
// No backward-compatible alias is emitted — a duplicate `id="1-1-definitions"`
// would reintroduce the exact invalid selector, and per the ruling is not wanted.
function headingId(s) {
  return "sec-" + (slug(s) || "section");
}

// GFM pipe-table helpers. Rows may be indented (the Codex nests tables inside
// numbered list items). A table is a header row followed by a delimiter row of
// dashes (e.g. | --- | :--: |). Cell text is passed through inlineNodes so
// bold/links/etc. render and the legal text is preserved verbatim.
function splitTableRow(line) {
  var s = line.trim();
  if (s.charAt(0) === "|") { s = s.slice(1); }
  if (s.charAt(s.length - 1) === "|") { s = s.slice(0, -1); }
  return s.split("|").map(function (c) { return c.trim(); });
}

function isTableSeparator(line) {
  if (!line || line.indexOf("|") === -1) { return false; }
  var cells = splitTableRow(line);
  if (!cells.length) { return false; }
  return cells.every(function (c) { return /^:?-{1,}:?$/.test(c); });
}

function inlineNodes(s) {
  var nodes = [];
  var rest = s;
  var re = /(`[^`]+`)|(\[[^\]]+\]\([^)\s]+\))|(\*\*[^*]+\*\*)|(__[^_]+__)|(\*[^*]+\*)/;
  while (rest.length) {
    var m = re.exec(rest);
    if (!m) { nodes.push(rest); break; }
    if (m.index > 0) { nodes.push(rest.slice(0, m.index)); }
    var tok = m[0];
    if (tok.charAt(0) === "`") { nodes.push({ n: "code", i: tok.slice(1, -1) }); }
    else if (tok.charAt(0) === "[") {
      var mm = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(tok);
      nodes.push({ n: "a", href: mm[2], i: mm[1] });
    } else if (tok.slice(0, 2) === "**" || tok.slice(0, 2) === "__") {
      nodes.push({ n: "strong", i: tok.slice(2, -2) });
    } else {
      nodes.push({ n: "em", i: tok.slice(1, -1) });
    }
    rest = rest.slice(m.index + tok.length);
  }
  if (nodes.length === 1 && typeof nodes[0] === "string") { return nodes[0]; }
  return nodes.length ? nodes : "";
}

exports.render = function render(md) {
  var lines = String(md).replace(/\r\n/g, "\n").split("\n");
  var out = [], toc = [], title = "";
  var i = 0, n = lines.length;

  /* HEADING DEPTH IS RELATIVE TO THE DOCUMENT, NOT ABSOLUTE.
   *
   * Both legal documents run through this one renderer, and they do not agree
   * on what a top-level section is. The Codex opens its Parts with `# Part 1`
   * and their subsections with `## 1.5`; the BAA has no H1 but its own title,
   * and opens every one of its twelve sections with `## 1. Purpose and Scope`.
   *
   * Keying off the absolute level treated the BAA's sections as if they were
   * Codex subsections: every entry in its TOC rail came out `is-sub` (indented
   * and dimmed to 46% white, so the rail had no primary level at all), and
   * every section rendered as an <h3> directly under the page <h1>, skipping
   * <h2> entirely.
   *
   * So find the shallowest heading the document actually uses — ignoring the
   * leading H1 that becomes the page title, which is not part of the body
   * outline — and measure from there. `rel === 0` is a top-level section
   * whatever its markdown level. The Codex is unaffected: it uses H1 for Parts,
   * so its base is still 1 and every tag and TOC level it produced before is
   * produced now. A document with no headings at all never reaches this. */
  var baseLevel = 6, sawTitleH1 = false;
  for (var scan = 0; scan < n; scan++) {
    var sh = lines[scan].match(/^(#{1,6})\s+\S/);
    if (!sh) { continue; }
    var slvl = sh[1].length;
    if (slvl === 1 && !sawTitleH1) { sawTitleH1 = true; continue; }   // the title
    if (slvl < baseLevel) { baseLevel = slvl; }
  }
  if (baseLevel === 6) { baseLevel = 1; }
  var para = [], listType = null, listBuf = [];
  // H4 — list state. `listStart` carries the SOURCE ordinal of the list's first
  // item so an interrupted list resumes at its real number instead of 1;
  // `listLoose` records that at least one blank line was absorbed inside the
  // list, which the skin uses to keep the pre-merge vertical rhythm.
  var listStart = null, listLoose = false, pendingBlank = false;

  function flushPara() {
    if (para.length) { out.push({ n: "p", c: "egt-doc__p", i: inlineNodes(para.join(" ")) }); para = []; }
  }
  function flushList() {
    if (listBuf.length) {
      var node = { n: listType, c: "egt-doc__list" + (listLoose ? " egt-doc__list--loose" : ""), i: listBuf.slice() };
      // Strings only: a UA/JSON attribute set to null drops the whole node.
      if (listType === "ol" && listStart) { node.start = listStart; }
      out.push(node);
    }
    listBuf = []; listType = null; listStart = null; listLoose = false;
  }
  function flush() { flushPara(); flushList(); }

  while (i < n) {
    var line = lines[i];

    // H4 — a blank line no longer closes an open list. The Codex writes every
    // ordered list LOOSE (item, blank line, item), so flushing here emitted one
    // single-item <ol> per item and each legitimately restarted at 1. The blank
    // still ends a paragraph; the list is closed by the next block that is not
    // a list item (heading/hr/table/quote/code/paragraph all call flush()).
    if (/^\s*$/.test(line)) { flushPara(); pendingBlank = true; i++; continue; }
    var afterBlank = pendingBlank;
    pendingBlank = false;

    if (/^```/.test(line)) {
      flush(); var code = []; i++;
      while (i < n && !/^```/.test(lines[i])) { code.push(lines[i]); i++; }
      out.push({ n: "pre", c: "egt-doc__pre", i: { n: "code", i: code.join("\n") } }); i++; continue;
    }

    if (line.indexOf("|") > -1 && i + 1 < n && isTableSeparator(lines[i + 1])) {
      flush();
      var thead = splitTableRow(line);
      i += 2;
      var trows = [];
      while (i < n && lines[i].indexOf("|") > -1 && !/^\s*$/.test(lines[i]) && !isTableSeparator(lines[i])) {
        trows.push(splitTableRow(lines[i])); i++;
      }
      out.push({ n: "div", c: "egt-doc__table-wrap", i: {
        n: "table", c: "egt-doc__table",
        i: [
          { n: "thead", i: { n: "tr", i: thead.map(function (c) { return { n: "th", i: inlineNodes(c) }; }) } },
          { n: "tbody", i: trows.map(function (r) { return { n: "tr", i: r.map(function (c) { return { n: "td", i: inlineNodes(c) }; }) }; }) }
        ]
      } });
      continue;
    }

    var h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      flush();
      var lvl = h[1].length, txt = h[2].trim(), id = headingId(txt);
      if (lvl === 1 && !title) { title = txt; i++; continue; }
      // Depth below the document's own top-level section (see baseLevel above).
      var rel = Math.max(0, lvl - baseLevel);
      var tag = "h" + Math.min(rel + 2, 6);
      // The skin class stays keyed to the MARKDOWN level, not the relative one:
      // egt.legal.scss sizes `--1`/`--2`/`--3` and the two documents already
      // look right under it. Only the tag and the TOC level are relative.
      out.push({ n: tag, id: id, c: "egt-doc__h egt-doc__h--" + lvl, i: inlineNodes(txt) });
      if (rel === 0) { toc.push({ id: id, text: txt, sub: false }); }
      else if (rel === 1) { toc.push({ id: id, text: txt, sub: true }); }
      i++; continue;
    }

    if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) { flush(); out.push({ n: "hr", c: "egt-doc__hr" }); i++; continue; }

    if (/^>\s?/.test(line)) {
      flush(); var bq = [];
      while (i < n && /^>\s?/.test(lines[i])) { bq.push(lines[i].replace(/^>\s?/, "")); i++; }
      out.push({ n: "blockquote", c: "egt-doc__bq", i: inlineNodes(bq.join(" ")) }); continue;
    }

    var ul = line.match(/^\s*[-*+]\s+(.*)$/);
    var ol = line.match(/^\s*(\d+)\.\s+(.*)$/);
    if (ul || ol) {
      flushPara();
      var t = ul ? "ul" : "ol";
      if (listType && listType !== t) { flushList(); }
      // Absorbed a blank line inside a list of the same type -> loose list.
      if (listType && afterBlank) { listLoose = true; }
      listType = t;
      var item = { n: "li", i: inlineNodes(ul ? ul[1] : ol[2]) };
      if (ol) {
        var num = String(parseInt(ol[1], 10));
        item.value = num;                       // string: null would drop the node
        if (listStart === null) { listStart = num; }
      }
      listBuf.push(item);
      i++; continue;
    }

    flushList(); para.push(line.trim()); i++;
  }
  flush();
  return { title: title || "The EGT Codex", toc: toc, nodes: out };
};
